import React, { useMemo } from 'react';
import {
  calculateConfiguratorPrice,
  projectTypes,
  qualityLevels,
  DESIGN_STYLES,
  formatTotalAsRange,
  ESTIMATE_PRICE_CONTEXT_UA,
  ESTIMATE_PRICE_CONTEXT_EN,
} from '../../utils/estimator';

function labelProjectType(id, isUA) {
  const p = projectTypes.find((x) => x.id === id);
  if (!p) return id;
  return isUA ? p.labelUA : p.labelEN;
}

function labelLevel(id, isUA) {
  const q = qualityLevels.find((x) => x.id === id);
  if (!q) return id;
  return isUA ? q.labelUA : q.labelEN;
}

function labelStyle(id, isUA) {
  const s = DESIGN_STYLES.find((x) => x.id === id);
  if (!s) return id;
  return isUA ? s.labelUA : s.labelEN;
}

function money(n) {
  return `$${Math.round(n).toLocaleString()}`;
}

export default function SummaryPanel({ isUA, estimateState }) {
  const pricing = useMemo(() => calculateConfiguratorPrice(estimateState), [estimateState]);

  const m2 = isUA ? 'м²' : 'm²';
  const showRoomsLine = pricing.roomSurcharge > 0;
  const showStyleLine = pricing.styleMultiplier !== 1 && pricing.subtotalBeforeStyle > 0;
  const showExtrasLine = pricing.extrasTotal > 0;

  const totalRange = formatTotalAsRange(pricing.total);
  const context = isUA ? ESTIMATE_PRICE_CONTEXT_UA : ESTIMATE_PRICE_CONTEXT_EN;

  const breakdownHint = isUA
    ? 'Впливає на склад та глибину опрацювання дизайну'
    : 'Affects the depth and scope of design development';

  const lblBase = isUA ? 'Базовий дизайн-пакет' : 'Base design package';
  const lblRooms = isUA ? 'Додаткові приміщення' : 'Additional rooms';
  const lblStyle = isUA ? 'Стилістичний рівень' : 'Style level';
  const lblExtras = isUA ? 'Додаткові послуги' : 'Additional services';

  return (
    <aside
      className="border border-border bg-background p-6 sm:p-8 lg:sticky lg:top-28 lg:z-0 transition-opacity duration-500"
      data-testid="cfg-summary"
    >
      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
        {isUA ? 'Підсумок' : 'Summary'}
      </p>

      <dl className="space-y-4 text-sm text-foreground/85">
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">{isUA ? 'Тип' : 'Type'}</dt>
          <dd className="text-right">{labelProjectType(estimateState.projectType, isUA)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">{isUA ? 'Площа' : 'Area'}</dt>
          <dd className="text-right tabular-nums">
            {estimateState.area} {m2}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">{isUA ? 'Рівень оздоблення' : 'Finish level'}</dt>
          <dd className="text-right">{labelLevel(estimateState.level, isUA)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">{isUA ? 'Планування' : 'Layout'}</dt>
          <dd className="text-right tabular-nums">
            {isUA
              ? `${estimateState.bedrooms} сп. · ${estimateState.bathrooms} санв.`
              : `${estimateState.bedrooms} bed · ${estimateState.bathrooms} bath`}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">{isUA ? 'Стиль' : 'Style'}</dt>
          <dd className="text-right">{labelStyle(estimateState.style, isUA)}</dd>
        </div>
      </dl>

      <div className="mt-8 pt-6 border-t border-border/60">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground/80 mb-3">
          {isUA ? 'Склад вартості' : 'How this estimate is built'}
        </p>
        <p className="text-[11px] text-muted-foreground/65 leading-relaxed mb-4">{breakdownHint}</p>
        <div className="space-y-3 text-[12px] leading-snug">
          <div className="flex justify-between gap-4 text-muted-foreground">
            <span>{lblBase}</span>
            <span className="tabular-nums text-foreground/90 shrink-0">{money(pricing.baseFromArea)}</span>
          </div>
          {showRoomsLine ? (
            <div className="flex justify-between gap-4 text-muted-foreground">
              <span>{lblRooms}</span>
              <span className="tabular-nums text-foreground/90 shrink-0">+{money(pricing.roomSurcharge)}</span>
            </div>
          ) : null}
          {showStyleLine ? (
            <div className="flex justify-between gap-4 text-muted-foreground">
              <span>{lblStyle}</span>
              <span className="tabular-nums text-foreground/90 shrink-0">+{money(pricing.styleAdjustment)}</span>
            </div>
          ) : null}
          {showExtrasLine ? (
            <div className="flex justify-between gap-4 text-muted-foreground">
              <span>{lblExtras}</span>
              <span className="tabular-nums text-foreground/90 shrink-0">+{money(pricing.extrasTotal)}</span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-8 pt-8 border-t border-border/80">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {isUA ? 'Орієнтовний діапазон' : 'Indicative range'}
        </p>
        <p className="font-display text-2xl sm:text-3xl text-foreground tracking-tight tabular-nums leading-tight">
          {totalRange}
        </p>
        <p className="mt-4 text-[11px] text-muted-foreground/80 leading-relaxed">{context}</p>
      </div>
    </aside>
  );
}
